import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TroubleshootingGuide } from './TroubleshootingGuide';
import { KeyboardShortcutsReference } from './KeyboardShortcutsReference';
import { Zap, Wrench, CheckCircle, Key, Play, BookOpen } from 'lucide-react';

export function HelpDocumentation() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground mb-2">Workflow Automation Guide</h2>
        <p className="text-muted-foreground">Learn how to create and manage workflows</p>
      </div>

      <Tabs defaultValue="getting-started" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="getting-started">Getting Started</TabsTrigger>
          <TabsTrigger value="nodes">Nodes & Types</TabsTrigger>
          <TabsTrigger value="secrets">Secrets</TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="keyboard">Keyboard</TabsTrigger>
          <TabsTrigger value="troubleshooting">Troubleshooting</TabsTrigger>
        </TabsList>

        {/* Getting Started */}
        <TabsContent value="getting-started" className="space-y-4">
          <Card className="p-6 space-y-4">
            <h3 className="text-xl font-semibold text-foreground">Getting Started</h3>
            <div className="space-y-4">
              {[
                {
                  step: 1,
                  title: 'Create a Workflow',
                  desc: 'Click "New" in the header to create a new workflow. Give it a meaningful name.',
                },
                {
                  step: 2,
                  title: 'Add Nodes',
                  desc: 'Right-click on the canvas to add nodes. Every workflow needs at least one trigger.',
                },
                {
                  step: 3,
                  title: 'Configure Nodes',
                  desc: 'Click a node to select it and configure its settings in the right panel.',
                },
                {
                  step: 4,
                  title: 'Connect Nodes',
                  desc: 'Use the "Connect Node" button to create connections between nodes.',
                },
                {
                  step: 5,
                  title: 'Run Your Workflow',
                  desc: 'Click "Run" to execute your workflow and see the results.',
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    {item.step}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 space-y-4 bg-blue-500/5 border-blue-500/20">
            <h4 className="font-semibold text-foreground flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Tips & Best Practices
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
              <li>Always start with a trigger node</li>
              <li>Use meaningful names for nodes to track your workflow</li>
              <li>Test with simple workflows first before building complex ones</li>
              <li>Save your workflow frequently</li>
              <li>Use the Logs tab to debug issues</li>
            </ul>
          </Card>
        </TabsContent>

        {/* Nodes & Types */}
        <TabsContent value="nodes" className="space-y-4">
          <div className="space-y-4">
            {/* Triggers */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-foreground">Triggers</h3>
              </div>
              <div className="space-y-4">
                {[
                  {
                    name: 'Manual Trigger',
                    desc: 'Start workflow by clicking the Run button',
                  },
                  {
                    name: 'Webhook Trigger',
                    desc: 'Receive HTTP POST requests to start workflow',
                  },
                  {
                    name: 'Timer Trigger',
                    desc: 'Execute workflow at regular intervals (seconds)',
                  },
                  {
                    name: 'API Trigger',
                    desc: 'Trigger workflow from external API calls',
                  },
                ].map((trigger) => (
                  <div key={trigger.name} className="p-3 rounded border border-border">
                    <p className="font-medium text-foreground">{trigger.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{trigger.desc}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Tools */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="w-6 h-6 text-amber-600" />
                <h3 className="text-xl font-semibold text-foreground">Tools</h3>
              </div>
              <div className="space-y-4">
                {[
                  {
                    name: 'HTTP Request',
                    desc: 'Make API calls (GET, POST, PUT, DELETE)',
                  },
                  {
                    name: 'Transform',
                    desc: 'Transform data using JavaScript code',
                  },
                  {
                    name: 'Conditional',
                    desc: 'Branch execution based on conditions',
                  },
                  {
                    name: 'Delay',
                    desc: 'Pause workflow execution for specified duration',
                  },
                ].map((tool) => (
                  <div key={tool.name} className="p-3 rounded border border-border">
                    <p className="font-medium text-foreground">{tool.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{tool.desc}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Actions */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-semibold text-foreground">Actions</h3>
              </div>
              <div className="space-y-4">
                {[
                  {
                    name: 'Send Notification',
                    desc: 'Display notifications in the UI',
                  },
                  {
                    name: 'Log Output',
                    desc: 'Log data to console and workflow logs',
                  },
                  {
                    name: 'Call Webhook',
                    desc: 'Send data to external webhook URLs',
                  },
                  {
                    name: 'Store Data',
                    desc: 'Save data to browser localStorage',
                  },
                ].map((action) => (
                  <div key={action.name} className="p-3 rounded border border-border">
                    <p className="font-medium text-foreground">{action.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{action.desc}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Secrets */}
        <TabsContent value="secrets" className="space-y-4">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-6 h-6 text-green-600" />
              <h3 className="text-xl font-semibold text-foreground">Managing Secrets</h3>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-foreground mb-2">What are Secrets?</h4>
                <p className="text-sm text-muted-foreground">
                  Secrets are encrypted values stored securely in your browser. Use them to store API keys, tokens, and other sensitive information without exposing them in your workflow configuration.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">How to Use Secrets</h4>
                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                  <li>Go to the "Secrets" tab and add a new secret with a name and value</li>
                  <li>In HTTP Request headers, reference it using <code className="bg-muted px-1 rounded">$SECRET_NAME</code></li>
                  <li>The secret will be substituted at runtime</li>
                </ol>
              </div>

              <div className="p-3 rounded bg-amber-500/5 border border-amber-500/20">
                <p className="text-sm text-amber-700 font-medium">Security Note</p>
                <p className="text-sm text-amber-600 mt-1">
                  Secrets are encrypted and stored in your browser. Never share your workflow files if they contain sensitive information.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Examples */}
        <TabsContent value="examples" className="space-y-4">
          <Card className="p-6 space-y-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Common Workflow Examples</h3>

            <div className="space-y-4">
              {[
                {
                  name: 'API Request Logger',
                  steps: ['Manual Trigger → HTTP Request → Log Output'],
                  use: 'Fetch data from an API and log the results',
                },
                {
                  name: 'Webhook Relay',
                  steps: ['Webhook Trigger → Call Webhook'],
                  use: 'Receive webhooks and forward them to another service',
                },
                {
                  name: 'Data Transform Pipeline',
                  steps: ['Manual Trigger → Transform → Store Data'],
                  use: 'Transform incoming data and save to storage',
                },
                {
                  name: 'Conditional Execution',
                  steps: ['Manual Trigger → Conditional → Send Notification'],
                  use: 'Execute different paths based on conditions',
                },
                {
                  name: 'Scheduled Task',
                  steps: ['Timer Trigger → HTTP Request → Log Output'],
                  use: 'Execute a task at regular intervals',
                },
              ].map((example) => (
                <div key={example.name} className="p-4 rounded border border-border space-y-2">
                  <p className="font-medium text-foreground">{example.name}</p>
                  <p className="text-xs text-muted-foreground font-mono bg-muted/50 p-2 rounded">
                    {example.steps[0]}
                  </p>
                  <p className="text-sm text-muted-foreground">{example.use}</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Keyboard */}
        <TabsContent value="keyboard" className="space-y-4">
          <KeyboardShortcutsReference />
        </TabsContent>
        {/* Troubleshooting */}
        <TabsContent value="troubleshooting" className="space-y-4">
          <TroubleshootingGuide />
        </TabsContent>
      </Tabs>
    </div>
  );
}